import { useState } from "react";
import { toast } from "sonner";
import { FileCheck2, Loader2, Download, Calculator, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { getAttribution, getPartnerRef } from "@/lib/attribution";

const field = "h-11 bg-slate-950/60 border-slate-700";
const MAGNETS = {
  sba: { icon: FileCheck2, title: "SBA feasibility study checklist", text: "The 24 items lenders expect to see, in a printable PDF. Tick them off as you go." },
  fdd: { icon: ClipboardList, title: "Franchise FDD review checklist", text: "What to check in Items 1 through 23 of a Franchise Disclosure Document before you sign." },
  costs: { icon: Calculator, title: "Startup cost worksheet", text: "Typical cost items and ranges for this business, with space for your quotes. PDF + Excel with automatic totals." },
};
const FORMAT_LABEL = { pdf: "PDF", xlsx: "Excel" };

export const ChecklistOffer = ({ magnet = "sba", page, pageKind = "article", citySlug = "", compact = false }) => {
  const m = MAGNETS[magnet];
  const [f, setF] = useState({ name: "", email: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/public/checklist", { ...f, magnet, page, page_kind: pageKind, city_slug: citySlug, ref: getPartnerRef(), attribution: getAttribution() });
      const list = (r.data.downloads || []).map((d) => ({ ...d, url: `${process.env.REACT_APP_BACKEND_URL}${d.url}` }));
      setFiles(list);
      if (list.length) window.location.assign(list[0].url);
      toast.success("Your download is starting. We've emailed you a copy too.");
    } catch (err) { toast.error(errMsg(err, "Could not send the download")); } finally { setBusy(false); }
  };
  const Icon = m.icon;
  return (
    <aside className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-6 sm:p-7 flex flex-col" data-testid={`magnet-${magnet}`}>
      <div className="flex items-start gap-4 mb-5">
        <span className="h-11 w-11 shrink-0 rounded-xl bg-emerald-500 grid place-items-center"><Icon className="h-5 w-5 text-emerald-950" /></span>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-1">Free download</p>
          <h2 className="font-display text-xl font-bold tracking-tight">{m.title}</h2>
          <p className="text-sm text-slate-400 mt-1">{m.text}</p>
        </div>
      </div>
      {files ? (
        <div className="flex flex-wrap items-center gap-3" data-testid={`magnet-done-${magnet}`}>
          <p className="text-sm text-emerald-300 w-full">Done! Check your downloads, and your inbox for a copy.</p>
          {files.map((d) => <a key={d.format} href={d.url} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/50 px-4 py-2 text-sm text-emerald-300 hover:bg-emerald-500/10" data-testid={`magnet-download-${magnet}-${d.format}`}><Download className="h-4 w-4" />{FORMAT_LABEL[d.format]}</a>)}
        </div>
      ) : (
        <form onSubmit={submit} className={`grid grid-cols-1 gap-3 mt-auto ${compact ? "" : "sm:grid-cols-[1fr_1fr_auto]"}`} data-testid={`magnet-form-${magnet}`}>
          <Input required maxLength={120} placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid={`magnet-name-${magnet}`} />
          <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid={`magnet-email-${magnet}`} />
          <input tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
          <Button type="submit" disabled={busy} className="h-11 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400 font-semibold" data-testid={`magnet-submit-${magnet}`}>{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Get it free</Button>
          <p className={`text-[11px] text-slate-500 ${compact ? "" : "sm:col-span-3"}`}>We'll email you a copy and may follow up about your project. No spam.</p>
        </form>
      )}
    </aside>
  );
};
